import React, { useState } from "react";
import styles from "./ImageWithPlaceholder.module.css";

// Serve Cloudinary images compressed and right-sized: auto format (WebP/AVIF),
// auto quality, capped width. Cuts image weight ~60-80% with no visible loss.
// Non-Cloudinary URLs (demo assets, previews) pass through untouched.
const optimizeCloudinary = (url, width = 900) => {
  if (
    typeof url !== "string" ||
    !url.includes("res.cloudinary.com") ||
    !url.includes("/upload/") ||
    url.includes("/upload/f_auto") // already transformed
  ) {
    return url;
  }
  return url.replace("/upload/", `/upload/f_auto,q_auto,w_${width}/`);
};

/**
 * Renders an image coming from the API with a shimmer-skeleton placeholder.
 *
 * The shimmer is shown while the image is still loading and stays visible if
 * the image fails to load or has no src (e.g. while the API is down), so the
 * layout never collapses into a broken-image icon.
 *
 * Pass `as` to render through a styled-component (e.g. `as={ProductImage}`)
 * while keeping the placeholder behaviour. Any extra props are forwarded to
 * the underlying image element. Use `wrapperStyle` to size the placeholder box
 * for images whose height depends on the loaded image.
 */
const ImageWithPlaceholder = ({
  as: Img = "img",
  src,
  alt = "",
  className,
  style,
  wrapperStyle,
  ...rest
}) => {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  const optimizedSrc = optimizeCloudinary(src);
  const showPlaceholder = !src || errored || !loaded;

  return (
    <span className={styles.wrapper} style={wrapperStyle}>
      <Img
        {...rest}
        src={optimizedSrc}
        alt={alt}
        className={className}
        style={{ ...style, opacity: showPlaceholder ? 0 : 1 }}
        onLoad={() => setLoaded(true)}
        onError={() => setErrored(true)}
      />
      {showPlaceholder && <span className={styles.shimmer} aria-hidden="true" />}
    </span>
  );
};

export default ImageWithPlaceholder;
